
import React, { useEffect, useMemo, useState } from 'react';
import { useSessionStore } from '@/store/sessionStore';
import type { SessionInput } from '@/types/api';
import ConversationSection from './ConversationSection';
import { groupByConversationSection } from '@/utils/groupSessionInputs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, RotateCcw, X, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';

const ConversationDisplay: React.FC = () => {
    const { 
        currentSessionDetails, 
        activePlanInput, 
        setActivePlanInput,
        discardSelectionMode,
        setDiscardSelectionMode,
        selectedInputIds,
        clearSelection,
        discardFilterMode,
        setDiscardFilterMode,
        toggleDiscardedBatch,
        undoLastDiscardAction,
        lastDiscardAction,
        currentSessionId,
        setOrderedInputIds,
        setAutoScrollEnabled,
    } = useSessionStore();
    
    const inputs = currentSessionDetails?.sessionInputs || [];

    // Filter inputs based on discard filter mode
    const filteredInputs = useMemo(() => {
        if (discardFilterMode === 'in_context') {
            return inputs.filter(inp => inp.is_discarded !== true);
        }
        return inputs;
    }, [inputs, discardFilterMode]);

    // Group inputs into conversation sections - memoized to avoid recalculation
    const sections = useMemo(() => {
        if (!filteredInputs.length) return [];
        return groupByConversationSection(filteredInputs);
    }, [filteredInputs]);

    // Flatten all messages for keyboard navigation - memoized
    const allMessages: SessionInput[] = useMemo(() => {
        const messages: SessionInput[] = [];
        for (const section of sections) {
            messages.push(section.userInput);
            messages.push(...section.assistantResponses);
        }
        return messages;
    }, [sections]);

    // Update ordered input IDs for range selection
    useEffect(() => {
        if (allMessages.length === 0) return; // Guard: skip empty to prevent unnecessary updates
        const orderedIds = allMessages.map(m => m.id);
        setOrderedInputIds(orderedIds);
    }, [allMessages, setOrderedInputIds]);

    // Count selected items
    const selectedCount = selectedInputIds.length;
    
    // Check if any selected items are discarded (to show restore button)
    const hasSelectedDiscarded = inputs.some(inp => selectedInputIds.includes(inp.id) && inp.is_discarded === true);
    const hasSelectedNotDiscarded = inputs.some(inp => selectedInputIds.includes(inp.id) && inp.is_discarded !== true);

    // Check if any selected discarded items are summarized sources with an active replacement
    // (cannot restore these — must restore the summary first)
    const hasSelectedSummarizedSource = inputs.some(inp => {
        if (!selectedInputIds.includes(inp.id) || inp.is_discarded !== true) return false;
        if (!inp.replaced_by_summary_input_id) return false;
        const replacement = inputs.find(i => i.id === inp.replaced_by_summary_input_id);
        return replacement && !replacement.is_discarded;
    });

    // Whether there are selected discarded items that CAN be restored (not under active summary)
    const hasSelectedDiscardedWithoutSummary = inputs.some(inp => {
        if (!selectedInputIds.includes(inp.id) || inp.is_discarded !== true) return false;
        if (!inp.replaced_by_summary_input_id) return true; // Not a summarized source
        const replacement = inputs.find(i => i.id === inp.replaced_by_summary_input_id);
        return !replacement || replacement.is_discarded; // Summary is gone/discarded
    });

    // Keyboard navigation - must be before any early returns
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Skip keyboard navigation when in selection mode
            if (discardSelectionMode) return;
            
            const target = e.target as HTMLElement;
            if (target.matches('input, textarea') || target.isContentEditable) {
                return;
            }

            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                if (allMessages.length === 0) return;

                e.preventDefault();

                const currentIndex = allMessages.findIndex(m => m.id === activePlanInput?.id);
                let nextIndex = currentIndex;

                if (currentIndex === -1) {
                    nextIndex = e.key === 'ArrowUp' ? allMessages.length - 1 : 0;
                } else {
                    if (e.key === 'ArrowUp') {
                        nextIndex = currentIndex - 1;
                        if (nextIndex < 0) nextIndex = 0;
                    } else {
                        nextIndex = currentIndex + 1;
                        if (nextIndex >= allMessages.length) nextIndex = allMessages.length - 1;
                    }
                }

                const nextMessage = allMessages[nextIndex];
                if (nextMessage) {
                    setActivePlanInput(nextMessage);
                    setAutoScrollEnabled(false);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [allMessages, activePlanInput, setActivePlanInput, discardSelectionMode]);

    // Undo toast - show after discard action
    useEffect(() => {
        if (lastDiscardAction && currentSessionId) {
            const toastId = toast(
                <div className="flex items-center gap-2">
                    <span>{lastDiscardAction.input_ids.length} message(s) updated</span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            undoLastDiscardAction(currentSessionId);
                            toast.dismiss(toastId);
                        }}
                    >
                        Undo
                    </Button>
                </div>,
                { duration: 5000 }
            );
        }
    }, [lastDiscardAction, currentSessionId, undoLastDiscardAction]);

    // Handle discard action
    const handleDiscard = async () => {
        if (!currentSessionId || selectedCount === 0) return;
        await toggleDiscardedBatch(currentSessionId, true);
    };

    // Handle restore action
    const handleRestore = async () => {
        if (!currentSessionId || selectedCount === 0) return;
        await toggleDiscardedBatch(currentSessionId, false);
    };

    // Calculate cumulative input tokens at the start of each section
    const getCumulativeInputAtSectionStart = (sectionIndex: number): number => {
        if (sectionIndex === 0) return 0;
        
        const prevSection = sections[sectionIndex - 1];
        const prevMessages = [prevSection.userInput, ...prevSection.assistantResponses];
        const lastPrevMessage = prevMessages[prevMessages.length - 1];
        
        return lastPrevMessage?.input_token_count || 0;
    };

    // Early return after all hooks
    if (!currentSessionDetails || inputs.length === 0) {
        return (
            <p className="text-sm text-center text-slate-500 dark:text-slate-400 py-8">
                Conversation will appear here...
            </p>
        );
    }

    return (
        <div className="space-y-1 relative">
            {/* Header with filter and selection mode toggle */}
            <div className="flex items-center gap-2 px-1 mb-2">
                {/* Filter dropdown */}
                <Select value={discardFilterMode} onValueChange={(value) => setDiscardFilterMode(value as 'all' | 'in_context')}>
                    <SelectTrigger className="w-[140px] h-7 text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All messages</SelectItem>
                        <SelectItem value="in_context">In context only</SelectItem>
                    </SelectContent>
                </Select>
                
                {/* Selection mode toggle */}
                <Button
                    variant={discardSelectionMode ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setDiscardSelectionMode(!discardSelectionMode)}
                >
                    {discardSelectionMode ? <CheckSquare className="h-3 w-3 mr-1" /> : <Square className="h-3 w-3 mr-1" />}
                    {discardSelectionMode ? 'Cancel' : 'Select'}
                </Button>
                
                {/* Clear selection (when in selection mode with items selected) */}
                {discardSelectionMode && selectedCount > 0 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={clearSelection}
                    >
                        <X className="h-3 w-3 mr-1" />
                        Clear ({selectedCount})
                    </Button>
                )}
            </div>
            
            {/* Sections */}
            {sections.map((section, index) => {
                const isLatest = index === sections.length - 1;
                const cumulativeInputAtStart = getCumulativeInputAtSectionStart(index);
                
                return (
                    <ConversationSection
                        key={section.id}
                        sectionId={section.id}
                        userInput={section.userInput}
                        assistantResponses={section.assistantResponses}
                        isLatest={isLatest}
                        cumulativeInputAtStart={cumulativeInputAtStart}
                    />
                );
            })}
            
            {/* Batch action bar (fixed at bottom when selection mode is on and items selected) */}
            {discardSelectionMode && selectedCount > 0 && (
                <div className="sticky bottom-0 left-0 right-0 bg-slate-100 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 p-2 flex items-center gap-2 z-10">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                        {selectedCount} selected
                    </span>
                    <div className="flex-1" />
                    {hasSelectedNotDiscarded && (
                        <Button
                            variant="default"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={handleDiscard}
                        >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Discard from Context
                        </Button>
                    )}
                    {hasSelectedDiscarded && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={handleRestore}
                            disabled={hasSelectedSummarizedSource && !hasSelectedDiscardedWithoutSummary}
                            title={hasSelectedSummarizedSource && !hasSelectedDiscardedWithoutSummary ? "Cannot restore summarized sources — restore the summary first" : undefined}
                        >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Restore to Context
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ConversationDisplay;